import * as t from "io-ts";
import { apiCall } from "../APICall";
import { eq } from "fp-ts";
import { constFalse, constTrue } from "fp-ts/function";
import { DateFromISOString } from "io-ts-types/DateFromISOString";
import { nonEmptyArray } from "io-ts-types/nonEmptyArray";
import { DocumentMeta } from "../Common/documentAPI";
import { withFallback } from "io-ts-types/withFallback";
import { optionFromUndefined } from "../globalDomain";
import { NonEmptyString } from "io-ts-types/NonEmptyString";
import {
  NonNegativeInteger,
  PositiveInteger,
} from "@dbn/omnichannel-design-system-g4p";
import { ClientId, ClientName, OnlyUsersFromMyOPU } from "./domain";
import { number } from "io-ts";

const DocSignApplication = t.type({
  id: t.string,
  applicationId: t.string,
  initiatedByFullName: t.string,
  initiatedById: t.string,
  documentsNo: t.number,
  creationDate: DateFromISOString,
  expirationDate: DateFromISOString,
  validityDays: t.number,
  status: t.string,
});
export type DocSignApplication = t.TypeOf<typeof DocSignApplication>;

export const getDocSignApplications = apiCall({
  path: ["clients", "docSign", "applications"],
  inputEq: eq.fromEquals(constFalse),
  inputCodec: t.void,
  outputCodec: t.array(DocSignApplication),
});

export const getDocSignApplication = apiCall({
  path: ["clients", "docSign", "applications", "current"],
  inputEq: eq.fromEquals(constFalse),
  inputCodec: t.void,
  outputCodec: DocSignApplication,
});

const RestoreDocSignApplicationRequest = t.type({
  applicationId: t.string,
});
export const restoreDocSignApplication = apiCall({
  path: ["clients", "docSign", "applications", "restore"],
  inputEq: eq.fromEquals(constFalse),
  inputCodec: RestoreDocSignApplicationRequest,
  outputCodec: t.union([t.void, t.null, t.undefined]),
});
export const restoreDocSignApplicationClient = apiCall({
  path: ["clients", "docSign", "applications", "restore", "client"],
  inputEq: eq.fromEquals(constFalse),
  inputCodec: RestoreDocSignApplicationRequest,
  outputCodec: t.union([t.void, t.null, t.undefined]),
});

export const DocSignFlowType = t.keyof({
  remote: true,
  person: true,
});
export type DocSignFlowType = t.TypeOf<typeof DocSignFlowType>;

const CreateDocSignApplicationRequest = t.type({
  validityDays: t.number,
  validityMinutes: t.number,
  preContractConfirmationNeeded: t.boolean,
  flowType: DocSignFlowType,
});
export const createDocSignApplication = apiCall({
  path: ["clients", "docSign", "applications", "create"],
  inputEq: eq.fromEquals(constFalse),
  inputCodec: CreateDocSignApplicationRequest,
  outputCodec: t.union([t.void, t.null, t.undefined]),
});

export const cancelDocSignApplication = apiCall({
  path: ["clients", "docSign", "applications", "cancel"],
  inputEq: eq.fromEquals(constFalse),
  inputCodec: t.void,
  outputCodec: t.union([t.void, t.null, t.undefined]),
});

const DocSignDocument = t.type({
  id: t.string,
  name: t.string,
  type: t.string,
});
export type DocSignDocument = t.TypeOf<typeof DocSignDocument>;

export const getDocSignDocuments = apiCall({
  path: ["clients", "docSign", "documents"],
  inputEq: eq.fromEquals(constFalse),
  inputCodec: t.void,
  outputCodec: t.array(DocSignDocument),
});

const SelectDocSignDocumentsRequest = t.type({
  docIds: t.array(t.string),
});
export const selectDocSignDocuments = apiCall({
  path: ["clients", "docSign", "documents", "select"],
  inputEq: eq.fromEquals(constFalse),
  inputCodec: SelectDocSignDocumentsRequest,
  outputCodec: t.array(DocSignDocument),
});

export const getDocSignDocumentsSelected = apiCall({
  path: ["clients", "docSign", "documents", "selected"],
  inputEq: eq.fromEquals(constFalse),
  inputCodec: t.void,
  outputCodec: t.array(DocSignDocument),
});

const RemoveDocSignDocumentsSelectedRequest = t.type({
  docIds: t.array(t.string),
});
export const removeDocSignDocumentsSelected = apiCall({
  path: ["clients", "docSign", "documents", "selected", "remove"],
  inputEq: eq.fromEquals(constFalse),
  inputCodec: RemoveDocSignDocumentsSelectedRequest,
  outputCodec: t.array(DocSignDocument),
});

export const checkDocSignPushNotification = apiCall({
  path: ["clients", "docSign", "push", "available"],
  inputEq: eq.fromEquals(constFalse),
  inputCodec: t.void,
  outputCodec: t.boolean,
});

export const sendDocSignPushNotification = apiCall({
  path: ["clients", "docSign", "push", "send"],
  inputEq: eq.fromEquals(constFalse),
  inputCodec: t.void,
  outputCodec: t.number,
});

export const PreContractualDocumentMeta = t.intersection(
  [
    DocumentMeta,
    t.type({
      signatureRequired: withFallback(t.boolean, false),
      downloadUrl: optionFromUndefined(NonEmptyString),
      contractualDocument: optionFromUndefined(t.boolean),
    }),
  ],
  "PreContractualDocumentMeta"
);
export const PreContractualDocumentsListOutput = nonEmptyArray(
  PreContractualDocumentMeta
);
export const getDocuments = apiCall({
  path: ["clients", "docSign", "documents", "contractual"],
  inputEq: eq.fromEquals(constTrue),
  inputCodec: t.void,
  outputCodec: PreContractualDocumentsListOutput,
});

export const DocumentMarkReadInput = t.type({
  docId: NonEmptyString,
  read: t.boolean,
});
export const markReadDocument = apiCall({
  path: ["clients", "docSign", "documents", "markRead"],
  inputEq: eq.fromEquals(constFalse),
  inputCodec: DocumentMarkReadInput,
  outputCodec: t.union([t.void, t.null, t.undefined]),
});

const DTSListInput = t.type(
  {
    page: NonNegativeInteger,
    size: PositiveInteger,
    dtsListRequest: t.type(
      {
        ClientId,
        ClientName,
        OnlyUsersFromMyOPU,
      },
      "DTSListRequest"
    ),
  },
  "DTSListInput"
);

const eqDTSListInput = eq.getStructEq({
  page: eq.eqNumber,
  size: eq.eqNumber,
  dtsListRequest: eq.getStructEq({
    ClientId: eq.eqStrict,
    ClientName: eq.eqStrict,
    OnlyUsersFromMyOPU: eq.eqStrict,
  }),
});

const DTSListRecord = t.type({
  ClientId: PositiveInteger,
  ClientName: NonEmptyString,
  ClientUsername: NonEmptyString,
  OPUName: NonEmptyString,
  ActiveFlows: number,
  TotalFlows: number,
});

export type DTSListRecord = t.TypeOf<typeof DTSListRecord>;

const DTSListOutput = t.type(
  {
    content: t.array(DTSListRecord),
    totalPages: NonNegativeInteger,
    totalElements: NonNegativeInteger,
    last: t.boolean,
    size: NonNegativeInteger,
    number: NonNegativeInteger,
  },
  "DTSListOutput"
);

export const getDTSList = apiCall({
  inputCodec: DTSListInput,
  inputEq: eqDTSListInput,
  path: ["clients", "docSign", "getDocumentsList"],
  outputCodec: DTSListOutput,
});

export const ClientIdInput = t.type({
  clientId: PositiveInteger,
});

const eqClientId = eq.getStructEq({
  clientId: eq.eqStrict,
});

export const setClientId = apiCall({
  inputCodec: ClientIdInput,
  inputEq: eqClientId,
  path: ["clients", "docSign", "setClientId"],
  outputCodec: t.union([t.void, t.null, t.undefined]),
});

const GetClientName = t.type({
  clientName: t.string,
});

export const getDocSignClientName = apiCall({
  path: ["clients", "docSign", "getClientName"],
  inputEq: eq.fromEquals(constFalse),
  inputCodec: t.void,
  outputCodec: GetClientName,
});
